import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Layout } from "providers/Layout";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  DateInput,
  Input
} from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Loader } from "shared/components/Loader";
import { Subscriber } from "views/rent-reporting-id/types";

import { getErrorMessage } from "utils/error";
import { UnsubscribeModal } from "./components/UnsubscribeModal";
import { subscriberValidations } from "./validations";
import { useForm } from "shared/hooks";
import { demaskPhoneValue } from "shared/components/forms/Input/helpers/phone-mask";
import { Redirect } from "shared/components/Redirect";
import { useAuthContext } from "providers/Auth";
import { toast } from "react-toastify";
import { Switch } from "shared/components/forms/Switch";

export const EditSubscriptionDetails = () => {
  const { type, permissions } = useAuthContext();
  const { values, handleChange, register, onSubmit } = useForm(
    {
      lease_end_date: "",
      lease_month_to_month: false,
      monthly_rent_amount: 0,
      moved_out: false,
      move_out_date: "",
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      dob: ""
    },
    subscriberValidations
  );
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);

  const { renterId } = useParams();
  const navigate = useNavigate();
  const { data: renter, isFetching } = useQuery({
    queryKey: ["SUBSCRIBER_DETAIL", renterId],
    queryFn: () =>
      instance
        .get(`/portal/v1/customers/${renterId}`)
        .then(({ data: renter }: { data: Subscriber }) => {
          handleChange("first_name", () => renter.first_name || "")({});
          handleChange("last_name", () => renter.last_name || "")({});
          handleChange("phone", () => demaskPhoneValue(renter.phone) || "")({});
          handleChange("dob", () => renter.dob || "")({});
          handleChange("email", () => renter.email || "")({});
          handleChange("lease_end_date", () => renter.lease_end_date || "")({});
          handleChange(
            "monthly_rent_amount",
            () => (renter.current_month_payment?.cents || 0) / 100 || ""
          )({});
          handleChange("move_out_date", () => renter.move_out_date || "")({});

          return renter;
        })
  });

  const { mutate: updateDetails } = useMutation({
    mutationKey: ["SUBSCRIBERS", renterId],
    mutationFn: (data: {
      first_name: string;
      last_name: string;
      email: string;
      phone: string;
      dob: string;
      lease_end_date: string;
      monthly_rent_amount: number;
      move_out_date: string;
    }) => {
      return instance.patch(`/portal/v1/customers/${renterId}`, {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: demaskPhoneValue(data.phone),
        dob: data.dob,
        lease_end_date: data.lease_end_date,
        monthly_rent_amount: data.monthly_rent_amount,
        moved_out_date: data.move_out_date
      });
    },
    onSuccess: () => {
      toast.success("Successfully updated renter details");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const { mutate: handleUnsubscribe } = useMutation({
    mutationKey: ["UNSUBSCRIBE", renterId],
    mutationFn: () => {
      return instance.delete(`/portal/v1/customers/${renterId}`);
    },
    onSuccess: () => {
      toast.success("Successfully unsubscribed");
      setShowUnsubscribeModal(false);
      navigate("/rent-reporting");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  if (!permissions.canEditSubscriptionDetails) return <Redirect href="/" />;

  if (isFetching)
    return (
      <Layout
        headerClassName="py-0"
        header={
          <>
            <div className="border-b border-alpha-40 p-6">
              <BreadCrumbs
                crumbs={[
                  {
                    title: "Rent reporting",
                    href: "/rent-reporting"
                  },
                  {
                    title: renter?.name || "",
                    href: `/rent-reporting/${renterId}`
                  },
                  {
                    title: "Edit",
                    href: `/rent-reporting/${renterId}/edit`
                  }
                ]}
              />
            </div>
            <div className="flex items-center justify-between border-b border-alpha-100 p-6">
              <h1 className="text-xl font-bold text-content-1000">
                Edit Subscription Details
              </h1>
              <Button
                variant={ButtonVariant.TEXT}
                size={ButtonSize.BASE}
                disabled={!permissions.canUnsubscribeRenters}
              >
                Unsubscribe
              </Button>
            </div>
          </>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Link to={`/rent-reporting/${renterId}`}>
              <Button size={ButtonSize.BASE} variant={ButtonVariant.BASE}>
                Cancel
              </Button>
            </Link>
            <Button size={ButtonSize.BASE} variant={ButtonVariant.PRIMARY}>
              Save
            </Button>
          </div>
        }
      >
        <div className="flex justify-center">
          <Loader />
        </div>
      </Layout>
    );

  if (type.isSDK) return <Redirect href="/" />;

  return (
    <Layout
      headerClassName="py-0"
      header={
        <>
          <div className="border-b border-alpha-40 p-6">
            <BreadCrumbs
              crumbs={[
                {
                  title: "Rent reporting",
                  href: "/rent-reporting"
                },
                {
                  title: renter?.name || "",
                  href: `/rent-reporting/${renterId}`
                },
                {
                  title: "Edit",
                  href: `/rent-reporting/${renterId}/edit`
                }
              ]}
            />
          </div>
          <div className="flex items-center justify-between border-b border-alpha-100 p-6">
            <h1 className="text-xl font-bold text-content-1000">
              Edit Subscription Details
            </h1>
            <Button
              variant={ButtonVariant.TEXT}
              size={ButtonSize.BASE}
              onClick={() => setShowUnsubscribeModal(true)}
              disabled={!permissions.canUnsubscribeRenters}
            >
              Unsubscribe
            </Button>
          </div>
        </>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Link to={`/rent-reporting/${renterId}`}>
            <Button size={ButtonSize.BASE} variant={ButtonVariant.BASE}>
              Cancel
            </Button>
          </Link>
          <Button
            size={ButtonSize.BASE}
            variant={ButtonVariant.PRIMARY}
            type="submit"
            form="edit-subscription-details-form"
            disabled={type.isPMS}
          >
            Save
          </Button>
        </div>
      }
    >
      <form
        id="edit-subscription-details-form"
        onSubmit={onSubmit({
          onSuccess: (data) =>
            updateDetails({
              first_name: data.first_name,
              last_name: data.last_name,
              email: data.email,
              phone: data.phone,
              dob: data.dob,
              lease_end_date: data.lease_end_date,
              monthly_rent_amount: data.monthly_rent_amount,
              move_out_date: data.move_out_date
            }),
          onError: (e) => {
            toast.error(Object.values(e).join(", "));
          }
        })}
      >
        <h2 className="text-sm font-bold text-content-1000">
          Subscription Details
        </h2>
        <div className="mb-4 mt-2 border-b border-alpha-100" />
        <div className="max-w-[756px]">
          <div className="mb-6 flex gap-4">
            <div className="w-full">
              <DateInput
                id="lease_end_date"
                label="Lease end date"
                {...register("lease_end_date")}
                readOnly={Boolean(values.lease_month_to_month) || type.isPMS}
                hideOptional
              />
              <Checkbox
                label="It’s month to month / no end date"
                id="lease_month_to_month"
                checked={values.lease_month_to_month}
                disabled={type.isPMS}
                onChange={() => {
                  handleChange(
                    "lease_month_to_month",
                    (v) => v
                  )(!values.lease_month_to_month);
                }}
              />
            </div>
            <div className="w-full">
              <Input
                id="monthly_rent_amount"
                label="Current monthly rent"
                readOnly={type.isPMS}
                hideOptional
                {...register("monthly_rent_amount")}
              />
            </div>
          </div>
          <div className="mb-4">
            <Switch
              label="Add Move-Out Date"
              disabled={type.isPMS}
              checked={values.moved_out}
              onChange={() =>
                handleChange("moved_out", (v) => v)(!values.moved_out)
              }
            />
          </div>
          <div className="mb-10 grid grid-cols-2">
            <DateInput
              id="moved_out_date"
              disabled={type.isPMS}
              readOnly={Boolean(!values.moved_out)}
              hideOptional
              {...register("move_out_date")}
            />
          </div>
          <h2 className="text-sm font-bold text-content-1000">
            Personal Details
          </h2>
        </div>
        <div className="mb-4 mt-2 border-b border-alpha-100" />
        <div className="max-w-[756px]">
          <div className="mb-2 flex gap-4">
            <div className="w-full">
              <Input
                id="first_name"
                label="Legal first name"
                readOnly={type.isPMS}
                hideOptional
                {...register("first_name")}
              />
            </div>
            <div className="w-full">
              <Input
                id="last_name"
                label="Legal last name"
                readOnly={type.isPMS}
                hideOptional
                {...register("last_name")}
              />
            </div>
          </div>
          <div className="mb-2 flex gap-4">
            <div className="w-full">
              <Input
                id="email"
                label="Email"
                readOnly={type.isPMS}
                hideOptional
                {...register("email")}
              />
            </div>
            <div className="w-full">
              <Input
                id="phone"
                label="Phone"
                readOnly={type.isPMS}
                hideOptional
                {...register("phone")}
                placeholder="(123) 000-000"
                type="tel"
              />
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="w-full">
              <DateInput
                id="dob"
                readOnly={type.isPMS}
                label="Date of birth"
                hideOptional
                {...register("dob")}
              />
            </div>
          </div>
        </div>
      </form>
      <UnsubscribeModal
        opened={showUnsubscribeModal}
        onClose={() => setShowUnsubscribeModal(false)}
        handleUnsubscribe={handleUnsubscribe}
      />
    </Layout>
  );
};
